import DOMPurify from 'dompurify';

let hookAdded = false;

/**
 * Allowlist-based HTML sanitizer for content coming from API/DB.
 * Goal: prevent XSS while still allowing a small set of trusted iframes:
 * - Vimeo embeds:   https://player.vimeo.com/video/{id}
 * - YouTube embeds: https://www.youtube.com/embed/{id} (optionally with query params)
 * - Google gview:   https://docs.google.com/gview?embedded=true&url=...
 * - Direct S3 PDFs: specific buckets/prefixes, .pdf only
 * - Cloudinary PDFs: res.cloudinary.com and iangels-res.cloudinary.com, .pdf only
 *
 * IMPORTANT:
 * - For docs.google.com/gview we DO NOT rewrite the src, because rewriting via URL.toString()
 *   tends to percent-encode the inner `url=` value and has caused blank frames in practice.
 * - We remove ng-src and keep only src.
 */

function isAllowedVimeoIframe(url) {
  return (
    url.protocol === 'https:' &&
    url.hostname === 'player.vimeo.com' &&
    /^\/video\/\d+$/i.test(url.pathname)
  );
}

function isAllowedYouTubeIframe(url) {
  if (url.protocol !== 'https:') return false;

  const hostOk =
    url.hostname === 'www.youtube.com' ||
    url.hostname === 'youtube.com' ||
    url.hostname === 'www.youtube-nocookie.com';

  if (!hostOk) return false;

  return /^\/embed\/[A-Za-z0-9_-]+$/i.test(url.pathname);
}

function isAllowedS3PdfIframe(url) {
  const allowedHosts = new Set([
    's3-us-west-1.amazonaws.com',
    's3-eu-west-1.amazonaws.com',
  ]);

  const allowedPrefixes = [
    '/iangels-portfolio-updates/',
    '/iangles/updates/',
  ];

  return (
    url.protocol === 'https:' &&
    allowedHosts.has(url.hostname) &&
    allowedPrefixes.some((p) => url.pathname.startsWith(p)) &&
    url.pathname.toLowerCase().endsWith('.pdf')
  );
}

function isAllowedCloudinaryPdf(url) {
  const allowedHosts = new Set([
    'res.cloudinary.com',
    'iangels-res.cloudinary.com',
  ]);

  return (
    url.protocol === 'https:' &&
    allowedHosts.has(url.hostname) &&
    url.pathname.toLowerCase().endsWith('.pdf')
  );
}

function isAllowedGoogleGview(url) {
  if (!(url.protocol === 'https:' && url.hostname === 'docs.google.com' && url.pathname === '/gview')) {
    return false;
  }

  const embedded = url.searchParams.get('embedded');
  const inner = url.searchParams.get('url');
  if (embedded !== 'true' || !inner) return false;

  try {
    const innerUrl = new URL(inner);
    return (
      innerUrl.protocol === 'https:' &&
      (isAllowedCloudinaryPdf(innerUrl) || isAllowedS3PdfIframe(innerUrl))
    );
  } catch {
    return false;
  }
}

function looksLikeDangerousProtocol(value) {
  return /^(javascript:|vbscript:|data:text\/html)/i.test(String(value).trim());
}

export function sanitizeHtml(dirtyHtml) {
  if (!dirtyHtml) return '';

  if (!hookAdded) {
    DOMPurify.addHook('uponSanitizeElement', (node, data) => {
      if (data.tagName !== 'iframe') return;

      const rawSrc = node.getAttribute('src') || node.getAttribute('ng-src');
      if (!rawSrc) {
        node.parentNode?.removeChild(node);
        return;
      }

      const original = String(rawSrc).trim();
      const decodedForParsing = original.replace(/&amp;/g, '&');

      const normalizedForParsing =
        decodedForParsing.startsWith('//') ? `https:${decodedForParsing}` : decodedForParsing;

      let url;
      try {
        url = new URL(normalizedForParsing);
      } catch {
        node.parentNode?.removeChild(node);
        return;
      }

      const allowed =
        isAllowedVimeoIframe(url) ||
        isAllowedYouTubeIframe(url) ||
        isAllowedGoogleGview(url) ||
        isAllowedS3PdfIframe(url) ||
        isAllowedCloudinaryPdf(url);

      if (!allowed) {
        node.parentNode?.removeChild(node);
        return;
      }

      node.removeAttribute('ng-src');

      if (isAllowedGoogleGview(url)) {
        node.setAttribute('src', original); // keep original to avoid gview breakage
        node.setAttribute('loading', 'lazy');
        node.setAttribute('allowfullscreen', '');
        return;
      }

      node.setAttribute('src', url.toString());
      node.setAttribute('loading', 'lazy');
      node.setAttribute('allowfullscreen', '');

      if (url.hostname === 'player.vimeo.com') {
        node.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      }

      if (
        url.hostname === 'www.youtube.com' ||
        url.hostname === 'youtube.com' ||
        url.hostname === 'www.youtube-nocookie.com'
      ) {
        node.setAttribute(
          'allow',
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
        );
        node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      }
    });

    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
        node.setAttribute('rel', 'noopener noreferrer');
      }

      ['href', 'src', 'xlink:href'].forEach((attr) => {
        const val = node.getAttribute?.(attr);
        if (!val) return;
        if (looksLikeDangerousProtocol(val)) {
          node.removeAttribute(attr);
        }
      });
    });

    hookAdded = true;
  }

  return DOMPurify.sanitize(String(dirtyHtml), {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'src',
      'ng-src',
      'title',
      'frameborder',
      'allow',
      'allowfullscreen',
      'loading',
      'referrerpolicy',
      'width',
      'height',
      'style',
      'target',
      'rel',
      'xlink:href',
    ],
    FORBID_TAGS: ['script', 'style', 'object', 'embed', 'link', 'meta'],
  });
}
